<template>
	<div class="content-page content-page--role-history">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Role History
					<span class="content-page__title--detail" v-if="pageTitle"
						>- {{ pageTitle }}</span
					>
				</h2>
			</div>
			<Button
				v-if="hasResults"
				className="btn--has-icon"
				labelText="Export"
				:btnClick="exportAll"
				:iconClassName="'icon icon-download'"
				:iconBeforeLabel="true"
			></Button>
		</div>
		<div class="results-toolbar results-toolbar--variant-three">
			<HistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
				:searchWidth="310"
				:datePickerWidth="185"
				:ddUserWidth="'185px'"
				placeholder="Search by permission or status change"
			/>
			<div class="results-toolbar__counter" v-if="hasResults">
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>
		<Spinner
			v-if="loading || downloadingCSV"
			:processor="{ loading: true }"
			data-test="spinnerPageContent"
		/>
		<ScrollContainer
			v-if="hasResults && !loading"
			:class="{ 'is-disabled': downloadingCSV }"
			data-test="pageContent"
		>
			<Table
				class="table-container--rol-history"
				:sourceObject="tableSource"
				v-if="hasResults"
				@change="updatedPagination"
				@sort="updateDirection"
				:isArrowUp="true"
				:sortOptions="handleSort"
			>
				<template #col-group>
					<colgroup>
						<!-- Date/Time -->
						<col class="table__col table__col--date" />

						<!-- Action -->
						<col class="table__col table__col--action" />

						<!-- Changed by -->
						<col class="table__col table__col--changed-by" />
					</colgroup>
				</template>
				<template #body-timestamp="{ body }">
					<div class="role-history-data">
						<div class="role-history-data__date-time">
							{{ body.timestamp }}
						</div>
						<div class="role-history-data__text-emphasis">
							<strong>{{ body.differenceDateString }}</strong>
						</div>
					</div>
				</template>
				<template #body-action="{ body, index}">
					<div
						class="role-history-data__text-action"
						ref="actionWidthRef"
					>
						<div
							class="role-history-data__action-description"
							v-html="body.actionDescription"
						></div>
						<div
							class="role-history-data__action-detail-button"
							v-if="body.hasTruncate"
						>
							<a
								class="link"
								v-on:click.prevent="
									() => displayHistoryDetailModal(index)
								"
								>{{ body.totalChangeDescription }}
							</a>
						</div>
					</div>
				</template>
			</Table>
		</ScrollContainer>
		<LogRolHistoryDetailModal
			@close="closeLogHistoryDetailModal"
			:logs="fieldChanges"
			v-if="showLogHistoryDetailModal"
			data-test="logHistoryDetailModal"
		/>
		<NoResults v-show="!hasResults && !loading" />
	</div>
</template>

<script>
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Button from '@/components/forms/button/button';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Table from '@/components/shells/table/table';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import HistoryFilters from '@/pages/global-history-screen-filters';
import headerOptions from './header-options';
import LogRolHistoryDetailModal from '@/sections/roles/history/role-history-detail-modal.vue';
import { parseDateToUTC } from '@/utils/date/parseDate';
import {
	ROLE_HISTORY_LOGS,
	SET_ROLE_HISTORY_LOGS
} from '@/sections/roles/roles-mutation-types';
import { mapGetters, mapState, mapActions } from 'vuex';
import RolesLogs from '@/sections/roles/mixins/roles-logs';
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';
import _ from 'lodash';
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'RoleHistory',
	mixins: [RolesLogs],
	components: {
		Button,
		ResultCounter,
		HistoryFilters,
		Table,
		Breadcrumbs,
		Spinner,
		NoResults,
		ScrollContainer,
		LogRolHistoryDetailModal
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		SourceApplication: String,
		changedEntityId: [String, Number],
		recordStart: Number,
		maxCount: Number
	},
	data() {
		return {
			defaultUrlNames: [
				{ url: '/roles', name: 'Roles' },
				{ name: 'History' }
			],
			loading: true,
			filters: {},
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: undefined,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				sourceApplication: ['VueSupport'],
				entityType: ['RoleModel'],
				changedEntityId: this.changedEntityId,
				recordStart: 1,
				maxCount: 10
			},
			downloadingCSV: false,
			actionClientWidth: 0,
			showLogHistoryDetailModal: false,
			indexLogSeleted: 0,
			fieldChanges: {},
			windowWidth: window.innerWidth
		};
	},
	async created() {
		this.tableSource.headers = headerOptions;
		if (_.isEmpty(this.permissions)) {
			await this.setPermissions();
		}
		this.loadUsers();
	},
	computed: {
		...mapGetters('roles', ['permissions', ROLE_HISTORY_LOGS]),
		...mapState('historyLogs', ['users']),

		hasResults() {
			return this.resultCounter > 0 && !this.loading;
		},
		resultCounter() {
			return this.tableSource.source.totalCount;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		pageTitle() {
			return this[ROLE_HISTORY_LOGS].pageTitle || '';
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		}
	},
	methods: {
		...mapActions('roles', {
			fetchRoleLogs: SET_ROLE_HISTORY_LOGS,
			setPermissions: 'loadPermissions'
		}),
		...mapActions('historyLogs', ['getUsers']),
		async loadUsers() {
			const params = {
				entityType: 'RoleModel',
				changedEntityId: this.changedEntityId
			};
			await this.getUsers(params).catch(() => {});
		},
		shouldLoadUserApi() {
			const usersApi = this.users.map(user => user.value);
			const userLogs = [
				...new Set(
					this[ROLE_HISTORY_LOGS].items.map(x => x.changedByUserId)
				)
			];

			const isDiff =
				usersApi.length === userLogs.length &&
				usersApi.every(id => userLogs.includes(id));
			return !isDiff;
		},
		async fetchDataLogs() {
			this.loading = true;

			let query = removeObjectEmptyValues(this.filters);

			if (query.search) {
				if (query.search.toLowerCase() === 'active') {
					query.search = 'true';
				}
				if (query.search.toLowerCase() === 'inactive') {
					query.search = 'false';
				}
				if (
					query.search.toLowerCase() === 'removed' ||
					query.search.toLowerCase() === 'remove'
				) {
					query.search = 'delete';
				}
				if (query.search.toLowerCase() === 'added') {
					query.search = 'add';
				}
			}
			query.minDatetime = parseDateToUTC(query.minDatetime);
			query.maxDatetime = parseDateToUTC(query.maxDatetime, true);

			try {
				await this.fetchRoleLogs(query).then(async () => {
					if (this.shouldLoadUserApi()) {
						this.loadUsers();
					}
				});
				const roleHistoryLogs = _.cloneDeep(this[ROLE_HISTORY_LOGS]);
				if (!_.isEmpty(roleHistoryLogs)) {
					const parseSource = this.parseRolesHistoryLogs(
						roleHistoryLogs,
						false
					);
					this.tableSource.source = parseSource;
				}
			} finally {
				this.loading = false;
			}
		},
		async exportAll() {
			this.downloadingCSV = true;

			let query = removeObjectEmptyValues(this.filters);
			query.minDatetime = parseDateToUTC(query.minDatetime);
			query.maxDatetime = parseDateToUTC(query.maxDatetime, true);
			query.maxCount = 500000;

			try {
				await this.fetchRoleLogs(query);
			} finally {
				const roleHistoryLogs = _.cloneDeep(this[ROLE_HISTORY_LOGS]);
				if (!_.isEmpty(roleHistoryLogs)) {
					this.exportCSV(roleHistoryLogs);
				}
				this.downloadingCSV = false;
			}
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);
			let path = `/roles/${this.changedEntityId}/history`;

			this.$router
				.push({
					path: path,
					query: queries
				})
				.catch(() => {});
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
			this.filters.orderBy = sortValue.sortBy;
		},
		updatedPagination(value) {
			this.filters.recordStart = value.page;
			this.filters.maxCount = value.countPerPage;
		},
		closeLogHistoryDetailModal(value) {
			this.showLogHistoryDetailModal = value;
			this.indexLogSeleted = 0;
		},
		displayHistoryDetailModal(index) {
			this.indexLogSeleted = index;

			this.fieldChanges = this.tableSource.source.items[
				this.indexLogSeleted
			];

			this.showLogHistoryDetailModal = true;
		}
	},
	watch: {
		'filters.changedByUserId': {
			immediate: true,
			handler(val) {
				if (val) {
					this.filters.recordStart = 1;
				}
			}
		},
		filters: {
			deep: true,
			handler(newValue) {
				this.updateRoute(newValue);
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchDataLogs();
			}
		}
	}
};
</script>
<style lang="scss">
strong {
	@include body-text('regular-emphasis');
	text-transform: capitalize;
	font-size: $bd-txt-font-size-regular;
}
.role-history-data {
	&__action-description,
	&__text-emphasis {
		strong {
			font-weight: 600;
		}
	}
}
</style>

<style lang="scss" scoped>
.role-history-data {
	display: flex;
	flex-direction: column;
	gap: $gutter-sm;

	&__date-time {
		display: flex;
		flex-wrap: wrap;
		column-gap: $gutter-lg;
		row-gap: $gutter-sm;
		justify-content: space-between;
		white-space: pre;
	}
	&__text-emphasis,
	&__text-action {
		display: flex;
		justify-content: space-between;
	}
	&__action-description {
		text-transform: none;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	&__action-detail-button {
		text-transform: none;
		text-align: right;
		font-size: $bd-txt-font-size-sm;
		text-wrap: nowrap;
	}
}
.table-container--rol-history {
	::v-deep .table {
		table-layout: fixed;
	}
	.table__col {
		width: 100%;

		@include mq($from: md) {
			&--date {
				width: 200px;
			}
			&--action {
				width: auto;
			}
			&--changed-by {
				width: 165px;
			}
		}

		@include mq($from: lg) {
			&--date {
				width: 200px;
			}
			&--action {
				width: auto;
			}
			&--changed-by {
				width: 265px;
			}
		}
	}
}

.results-toolbar {
	width: 100%;
}
</style>
